import { useState, useCallback, useId } from 'react';
import { Check, FlaskConical, Flame, AlertTriangle, Eye, Droplets } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { saltFormationColors } from '@/config/experiments/chem1131/experiment6.config';

const stepInfoContent: Record<number, { title: string; content: string; whatHappens?: string; observe?: string[]; equation?: string }> = {
  1: {
    title: 'Examining Copper Wire',
    content: 'Copper (Cu) is a transition metal with characteristic reddish-brown color. Fresh copper has a metallic luster, but exposure to air causes oxidation, forming a dull tarnish of copper oxide (CuO) or copper carbonate (Cu2(OH)2CO3).',
    whatHappens: 'You observe the copper wire to note any tarnish or discoloration that indicates surface oxidation.',
    observe: ['Reddish-brown metallic color underneath', 'Possible dark spots (CuO) or green patina (carbonate)', 'Wire should be ductile and bendable']
  },
  2: {
    title: 'Cleaning Copper Wire',
    content: 'Steel wool is an abrasive that mechanically removes the oxide layer from the copper surface. A clean, oxide-free surface is essential for the direct combination reaction to proceed efficiently.',
    whatHappens: 'Abrasion removes the surface oxide layer, exposing fresh copper metal with its characteristic bright luster.',
    observe: ['Surface becomes shiny and bright', 'Copper color appears more vibrant', 'Wire is now ready for reaction']
  },
  3: {
    title: 'Preparing the Coil',
    content: 'Coiling the wire increases the surface area exposed to sulfur while keeping it compact enough to fit in the crucible. More surface area means faster and more complete reaction.',
    whatHappens: 'The wire is wound into a tight coil to maximize contact with sulfur powder.',
    observe: ['Coil should be tight and compact', 'Multiple turns maximize surface area', 'Shape should fit inside crucible']
  },
  4: {
    title: 'Adding Copper to Crucible',
    content: 'A crucible is a container that can withstand very high temperatures. Made of porite or ceramic, it allows us to heat reactants without damaging the container.',
    whatHappens: 'The copper coil is placed in the crucible in preparation for adding sulfur.',
    observe: ['Coil sits at bottom of crucible', 'Leave room for sulfur powder', 'Crucible sits on clay triangle']
  },
  5: {
    title: 'Adding Sulfur Powder',
    content: 'Sulfur (S) is a yellow nonmetal that exists as S8 rings. It has a low melting point (115C) and will react with copper when heated to form copper(II) sulfide.',
    whatHappens: 'Powdered sulfur is added to cover the copper coil, ensuring maximum contact between reactants.',
    observe: ['Yellow sulfur covers the copper', 'Add to a uniform depth of about 3 mm', 'Sulfur has a characteristic odor'],
    equation: 'Cu + S -> CuS (when heated)'
  },
  6: {
    title: 'Heating the Reaction',
    content: 'Heat provides the activation energy needed to start the reaction. Once initiated, the reaction between copper and sulfur is exothermic and may continue on its own.',
    whatHappens: 'The Bunsen burner heats the crucible. Sulfur melts first (115C), then reacts with copper at higher temperatures.',
    observe: ['Blue flame indicates good air/gas mixture', 'Sulfur melts to amber liquid', 'Temperature rises to 400-600C'],
    equation: 'Cu(s) + S(l) -> CuS(s) + heat'
  },
  7: {
    title: 'Observing the Reaction',
    content: 'Direct combination (synthesis) reactions involve two elements combining to form a compound. This is a redox reaction: copper is oxidized (loses electrons) and sulfur is reduced (gains electrons).',
    whatHappens: 'The molten sulfur reacts with hot copper to form black copper(II) sulfide. Excess sulfur may burn off as SO2 gas.',
    observe: ['Color changes from yellow/orange to black', 'May see blue sulfur flame', 'SO2 gas may form (work in fume hood!)'],
    equation: 'Cu + S -> CuS (black solid)'
  },
  8: {
    title: 'Cooling the Product',
    content: 'Hot crucibles must cool slowly to prevent thermal shock and cracking. The product must reach room temperature before testing to avoid damaging test equipment.',
    whatHappens: 'The crucible and contents cool to room temperature while the black CuS product solidifies.',
    observe: ['Product remains black after cooling', 'Crucible changes from red-hot to gray', 'Allow several minutes for complete cooling']
  },
  9: {
    title: 'Testing with NH₄OH',
    content: 'Ammonia (NH₄OH) forms a deep blue complex [Cu(NH₃)₄]²⁺ with Cu²⁺ ions in solution. CuS is insoluble and won not dissolve to release Cu²⁺ ions, so no blue color forms.',
    whatHappens: 'Adding ammonia solution tests whether the product contains free copper or copper sulfide.',
    observe: ['No blue color = confirms CuS formed', 'Blue color would indicate unreacted Cu²⁺', 'CuS is insoluble in ammonia'],
    equation: 'Cu²⁺ + 4NH₃ → [Cu(NH₃)₄]²⁺ (blue) - but CuS gives NO reaction'
  },
  10: {
    title: 'Identifying the Product',
    content: 'Copper(II) sulfide (CuS) is a black, insoluble compound. This synthesis reaction demonstrates how two elements can directly combine when energy is supplied.',
    whatHappens: 'The black product is confirmed to be copper(II) sulfide based on color, insolubility, and negative ammonia test.',
    observe: ['Black color characteristic of CuS', 'Insoluble in water and ammonia', 'Metal + nonmetal = ionic compound'],
    equation: 'Cu + S -> CuS (copper(II) sulfide)'
  }
};

export default function directCombination() {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [copperExamined, setCopperExamined] = useState(false);
  const [copperCleaned, setCopperCleaned] = useState(false);
  const [coilPrepared, setCoilPrepared] = useState(false);
  const [copperInCrucible, setCopperInCrucible] = useState(false);
  const [sulfurAdded, setSulfurAdded] = useState(false);
  const [heating, setHeating] = useState(false);
  const [heatingProgress, setHeatingProgress] = useState(0);
  const [reactionObserved, setReactionObserved] = useState(false);
  const [cooled, setCooled] = useState(false);
  const [nh4ohTested, setNh4ohTested] = useState(false);
  const [productIdentified, setProductIdentified] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const id = useId();

  const completedSteps = [
    copperExamined, copperCleaned, coilPrepared, copperInCrucible, sulfurAdded,
    reactionObserved, cooled, nh4ohTested, productIdentified
  ].filter(Boolean).length;

  const handleExamineCopper = useCallback(() => {
    if (currentStep === 1 && !copperExamined) {
      setCopperExamined(true);
      setCurrentStep(2);
    }
  }, [currentStep, copperExamined]);

  const handleCleanCopper = useCallback(() => {
    if (currentStep === 2 && !copperCleaned) {
      setCopperCleaned(true);
      setCurrentStep(3);
    }
  }, [currentStep, copperCleaned]);

  const handlePrepareCoil = useCallback(() => {
    if (currentStep === 3 && !coilPrepared) {
      setCoilPrepared(true);
      setCurrentStep(4);
    }
  }, [currentStep, coilPrepared]);

  const handleAddCopper = useCallback(() => {
    if (currentStep === 4 && !copperInCrucible) {
      setCopperInCrucible(true);
      setCurrentStep(5);
    }
  }, [currentStep, copperInCrucible]);

  const handleAddSulfur = useCallback(() => {
    if (currentStep === 5 && !sulfurAdded) {
      setSulfurAdded(true);
      setCurrentStep(6);
    }
  }, [currentStep, sulfurAdded]);

  const handleStartHeating = useCallback(() => {
    if (currentStep === 6 && sulfurAdded && !heating && !reactionObserved) {
      setHeating(true);
      setCurrentStep(7);

      let progress = 0;
      const interval = setInterval(() => {
        progress += 0.02;
        setHeatingProgress(progress);
        if (progress >= 1) {
          clearInterval(interval);
          setHeating(false);
          setReactionObserved(true);
          setCurrentStep(8);
        }
      }, 80);
    }
  }, [currentStep, sulfurAdded, heating, reactionObserved]);

  const handleCool = useCallback(() => {
    if (currentStep === 8 && reactionObserved && !cooled) {
      setCooled(true);
      setCurrentStep(9);
    }
  }, [currentStep, reactionObserved, cooled]);

  const handleTestNH4OH = useCallback(() => {
    if (currentStep === 9 && cooled && !nh4ohTested) {
      setNh4ohTested(true);
      setCurrentStep(10);
    }
  }, [currentStep, cooled, nh4ohTested]);

  const handleIdentifyProduct = useCallback(() => {
    if (currentStep === 10 && nh4ohTested && !productIdentified) {
      setProductIdentified(true);
    }
  }, [currentStep, nh4ohTested, productIdentified]);

  // Color transitions during heating
  const getContentsColor = () => {
    if (reactionObserved) return saltFormationColors.copperSulfide;
    if (heatingProgress > 0.7) return '#3A5A5A';
    if (heatingProgress > 0.4) return saltFormationColors.sulfurMelting;
    if (sulfurAdded) return saltFormationColors.sulfurPowder;
    if (copperInCrucible) return saltFormationColors.copperWireClean;
    return '#A08060';
  };

  const getTemperature = () => {
    if (reactionObserved) return 400;
    if (heating) return Math.round(100 + heatingProgress * 500);
    return 25;
  };

  const steps = [
    { id: 1, title: 'Examine Copper', hint: 'Observe the copper wire', done: copperExamined },
    { id: 2, title: 'Clean Copper', hint: 'Use steel wool', done: copperCleaned },
    { id: 3, title: 'Prepare Coil', hint: 'Wrap wire into coil', done: coilPrepared },
    { id: 4, title: 'Add to Crucible', hint: 'Place coil in crucible', done: copperInCrucible },
    { id: 5, title: 'Add Sulfur', hint: 'Add sulfur to ~3 mm depth', done: sulfurAdded },
    { id: 6, title: 'Heat', hint: 'Heat with Bunsen burner', done: heating || reactionObserved },
    { id: 7, title: 'Observe Reaction', hint: 'Watch sulfur react', done: reactionObserved },
    { id: 8, title: 'Cool', hint: 'Let crucible cool', done: cooled },
    { id: 9, title: 'Test with NH₄OH', hint: 'Add ammonium hydroxide', done: nh4ohTested },
    { id: 10, title: 'Identify Product', hint: 'Identify copper sulfide', done: productIdentified },
  ];

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part A: Direct Combination</h3>
        </div>
        <div className="safety-warning">
          <AlertTriangle size={14} />
          <span>Work in FUME HOOD - SO2 may form!</span>
        </div>
        <div className="steps-list">
          {steps.map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Color transition indicator */}
        <div className="color-transition">
          <span className="label">Color Change:</span>
          <div className="color-strip">
            <div className="swatch" style={{ background: saltFormationColors.copperWireClean }} title="Copper" />
            <span className="plus">+</span>
            <div className="swatch" style={{ background: saltFormationColors.sulfurPowder }} title="Sulfur" />
            <span className="arrow">→</span>
            <div className="swatch current" style={{ background: getContentsColor() }} title="Product" />
          </div>
        </div>
      </div>

      <LabBench
        experimentName="Cu + S Direct Combination"
        completedCount={completedSteps}
        totalSteps={10}
        equipment={['Copper wire (5 cm)', 'Steel wool', 'Pencil (for coiling wire)', 'Crucible', 'Clay triangle', 'Ring clamp', 'Bunsen burner', 'Powdered sulphur', '6 M NH₄OH', 'Test tube', 'Fume hood']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="reaction-workspace">
          <div className="equipment-row">
            {/* Copper Wire */}
            <div
              className={`equipment-station ${(currentStep <= 3 && !coilPrepared) ? 'clickable' : ''}`}
              onClick={() => {
                if (currentStep === 1) handleExamineCopper();
                else if (currentStep === 2) handleCleanCopper();
                else if (currentStep === 3) handlePrepareCoil();
              }}
            >
              <svg viewBox="0 0 60 80" width={50} height={70}>
                <defs>
                  <linearGradient id={`copper-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={copperCleaned ? saltFormationColors.copperWireClean : saltFormationColors.copperWireTarnished} />
                    <stop offset="50%" stopColor={copperCleaned ? '#CD8850' : '#9A5213'} />
                    <stop offset="100%" stopColor={copperCleaned ? saltFormationColors.copperWireClean : saltFormationColors.copperWireTarnished} />
                  </linearGradient>
                </defs>
                {coilPrepared ? (
                  <g>
                    <ellipse cx="30" cy="25" rx="18" ry="6" fill="none" stroke={`url(#copper-${id})`} strokeWidth="3" />
                    <ellipse cx="30" cy="33" rx="16" ry="5" fill="none" stroke={`url(#copper-${id})`} strokeWidth="3" />
                    <ellipse cx="30" cy="40" rx="14" ry="4" fill="none" stroke={`url(#copper-${id})`} strokeWidth="3" />
                    <ellipse cx="30" cy="46" rx="12" ry="3" fill="none" stroke={`url(#copper-${id})`} strokeWidth="3" />
                  </g>
                ) : (
                  <g>
                    <ellipse cx="30" cy="25" rx="20" ry="8" fill="none" stroke={`url(#copper-${id})`} strokeWidth="3" />
                    <ellipse cx="30" cy="35" rx="18" ry="7" fill="none" stroke={`url(#copper-${id})`} strokeWidth="3" />
                    <ellipse cx="30" cy="45" rx="16" ry="6" fill="none" stroke={`url(#copper-${id})`} strokeWidth="3" />
                  </g>
                )}
                {copperCleaned && (
                  <circle cx="42" cy="25" r="2" fill="#FFF" opacity="0.5">
                    <animate attributeName="opacity" values="0.5;0.2;0.5" dur="1.5s" repeatCount="indefinite" />
                  </circle>
                )}
                <text x="30" y="70" fontSize="8" fill="#E0E0E0" textAnchor="middle">
                  {coilPrepared ? 'Cu Coil' : copperCleaned ? 'Cleaned Cu' : 'Cu Wire'}
                </text>
              </svg>
            </div>

            {/* NH4OH Bottle */}
            <div
              className={`equipment-station ${currentStep === 9 && !nh4ohTested ? 'clickable' : ''}`}
              onClick={handleTestNH4OH}
            >
              <svg viewBox="0 0 50 80" width={45} height={75}>
                <rect x="12" y="20" width="26" height="50" fill="#E6F3FF" stroke="#888" strokeWidth="1" rx="2" />
                <rect x="17" y="10" width="16" height="12" fill="#444" rx="2" />
                <text x="25" y="48" fontSize="5" fill="#333" textAnchor="middle">NH₄OH</text>
                <text x="25" y="56" fontSize="4" fill="#666" textAnchor="middle">6 M</text>
                <text x="25" y="78" fontSize="7" fill="#E0E0E0" textAnchor="middle">Ammonia</text>
              </svg>
            </div>

            {/* Sulfur Container */}
            <div
              className={`equipment-station ${currentStep === 5 && !sulfurAdded ? 'clickable' : ''}`}
              onClick={handleAddSulfur}
            >
              <svg viewBox="0 0 50 70" width={45} height={65}>
                <rect x="10" y="15" width="30" height="40" fill="#444" stroke="#666" strokeWidth="1" rx="3" />
                <ellipse cx="25" cy="35" rx="12" ry="8" fill={saltFormationColors.sulfurPowder} />
                <text x="25" y="65" fontSize="7" fill="#E0E0E0" textAnchor="middle">Sulfur</text>
              </svg>
            </div>

            {/* Crucible on Burner Setup */}
            <div
              className={`equipment-station ${(currentStep === 4 || currentStep === 6 || currentStep === 8) ? 'clickable' : ''}`}
              onClick={() => {
                if (currentStep === 4) handleAddCopper();
                else if (currentStep === 6) handleStartHeating();
                else if (currentStep === 8) handleCool();
              }}
            >
              <svg viewBox="0 0 120 180" width={130} height={200}>
                <defs>
                  <linearGradient id={`crucible-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={heating ? '#D87060' : '#E8E0D5'} />
                    <stop offset="50%" stopColor={heating ? '#E89070' : '#F5F0E8'} />
                    <stop offset="100%" stopColor={heating ? '#D87060' : '#E8E0D5'} />
                  </linearGradient>
                  <linearGradient id={`flame-outer-${id}`} x1="0%" y1="100%" x2="0%" y2="0%">
                    <stop offset="0%" stopColor="#FF5A36" stopOpacity="0.9" />
                    <stop offset="30%" stopColor="#1E90FF" stopOpacity="0.85" />
                    <stop offset="70%" stopColor="#00BFFF" stopOpacity="0.7" />
                    <stop offset="100%" stopColor="#87CEEB" stopOpacity="0" />
                  </linearGradient>
                </defs>

                {/* Clay triangle */}
                <polygon points="60,75 30,115 90,115" fill="none" stroke={saltFormationColors.clayTriangle} strokeWidth="4" />

                {/* Crucible */}
                <g transform="translate(60, 60)">
                  <path
                    d="M-25 0 L-28 25 Q-28 40 0 45 Q28 40 28 25 L25 0"
                    fill={`url(#crucible-${id})`}
                    stroke={heating ? '#AA6040' : '#C0B8A8'}
                    strokeWidth="2"
                  />
                  {/* Crucible interior */}
                  <ellipse cx="0" cy="3" rx="22" ry="6" fill="#C8C0B0" />

                  {/* Contents */}
                  {(copperInCrucible || sulfurAdded || reactionObserved) && (
                    <ellipse cx="0" cy="10" rx="18" ry="5" fill={getContentsColor()}>
                      {heating && (
                        <animate
                          attributeName="fill"
                          values={`${saltFormationColors.sulfurPowder};${saltFormationColors.sulfurMelting};${saltFormationColors.copperSulfide}`}
                          dur="4s"
                          fill="freeze"
                        />
                      )}
                    </ellipse>
                  )}

                  {/* Sulfur melting animation */}
                  {heating && heatingProgress > 0.3 && heatingProgress < 0.7 && (
                    <g>
                      {[...Array(3)].map((_, i) => (
                        <circle
                          key={i}
                          cx={-8 + i * 8}
                          cy={8}
                          r={2}
                          fill={saltFormationColors.sulfurMelting}
                          opacity={0.7}
                        >
                          <animate attributeName="r" values="2;4;2" dur="0.5s" repeatCount="indefinite" />
                        </circle>
                      ))}
                    </g>
                  )}

                  {/* Glow when hot */}
                  {heating && (
                    <ellipse cx="0" cy="0" rx="28" ry="15" fill="rgba(255,100,50,0.3)">
                      <animate attributeName="opacity" values="0.3;0.5;0.3" dur="1s" repeatCount="indefinite" />
                    </ellipse>
                  )}
                </g>

                {/* Burner */}
                <g transform="translate(60, 165)">
                  {/* Burner tube */}
                  <rect x="-8" y="-45" width="16" height="40" fill="#5A5A5A" rx="2" />
                  <ellipse cx="0" cy="-45" rx="9" ry="3" fill="#2A2A2A" />

                  {/* Flame */}
                  {heating && (
                    <g>
                      <ellipse cx="0" cy="-60" rx="10" ry="20" fill={`url(#flame-outer-${id})`}>
                        <animate attributeName="ry" values="20;22;18;20" dur="0.2s" repeatCount="indefinite" />
                      </ellipse>
                      <ellipse cx="0" cy="-55" rx="5" ry="12" fill="#00CED1" opacity="0.8">
                        <animate attributeName="ry" values="12;14;10;12" dur="0.15s" repeatCount="indefinite" />
                      </ellipse>
                    </g>
                  )}

                  {/* Base */}
                  <ellipse cx="0" cy="5" rx="20" ry="5" fill="#3A3A3A" />
                </g>

                <text x="60" y="178" fontSize="9" fill="#E0E0E0" textAnchor="middle">
                  {heating ? `${getTemperature()}C` : 'Crucible + Burner'}
                </text>
              </svg>
            </div>
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {currentStep === 1 && !copperExamined && (
              <div className="status-message info">
                <Eye size={20} />
                Examine the copper wire - note tarnish or oxidation
              </div>
            )}
            {currentStep === 2 && !copperCleaned && (
              <div className="status-message info">
                <Flame size={20} />
                Clean the copper wire with steel wool to remove tarnish
              </div>
            )}
            {currentStep === 3 && !coilPrepared && (
              <div className="status-message info">
                <FlaskConical size={20} />
                Form the copper wire into a tight coil
              </div>
            )}
            {currentStep === 4 && !copperInCrucible && (
              <div className="status-message info">
                <FlaskConical size={20} />
                Place the copper coil in the crucible
              </div>
            )}
            {currentStep === 5 && !sulfurAdded && (
              <div className="status-message info">
                <FlaskConical size={20} />
                Add sulfur powder to a uniform depth of about 3 mm
              </div>
            )}
            {currentStep === 6 && !heating && !reactionObserved && (
              <div className="status-message warning">
                <AlertTriangle size={20} />
                In fume hood: Click to heat and watch the reaction!
              </div>
            )}
            {heating && (
              <div className="status-message reaction">
                <Flame size={20} />
                Heating... Sulfur melting and reacting with copper
              </div>
            )}
            {currentStep === 8 && reactionObserved && !cooled && (
              <div className="status-message info">
                <FlaskConical size={20} />
                Click crucible to let it cool down
              </div>
            )}
            {currentStep === 9 && cooled && !nh4ohTested && (
              <div className="status-message info">
                <Droplets size={20} />
                Test the product with NH₄OH (no blue = CuS not Cu)
              </div>
            )}
            {currentStep === 10 && nh4ohTested && !productIdentified && (
              <div
                className="status-message success"
                onClick={handleIdentifyProduct}
                style={{ cursor: 'pointer' }}
              >
                <Check size={20} />
                Click to confirm: Product is Copper(II) Sulfide (CuS)
              </div>
            )}
            {productIdentified && (
              <div className="status-message success">
                <Check size={20} />
                Complete! CuS formed by direct combination.
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Direct Combination (Synthesis)</h4>
            <div className="equation">
              Cu + S → CuS (copper(II) sulfide)
            </div>
            <p>
              When two elements combine directly to form a compound, this is called a synthesis
              or combination reaction. The copper-colored metal and yellow sulfur combine when
              heated to form black copper(II) sulfide.
            </p>
          </div>
        </div>

        <style>{`
          .reaction-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .equipment-row {
            display: flex;
            gap: 40px;
            align-items: flex-end;
            justify-content: center;
          }

          .equipment-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .equipment-station.clickable {
            cursor: pointer;
          }

          .equipment-station.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .safety-warning {
            display: flex;
            align-items: center;
            gap: 8px;
            background: rgba(239, 68, 68, 0.2);
            color: #EF4444;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 11px;
            margin: 8px 0;
          }

          .color-transition {
            margin-top: 12px;
            padding: 10px;
            background: var(--bg-elevated);
            border-radius: 6px;
          }

          .color-transition .label {
            font-size: 11px;
            color: var(--text-secondary);
            display: block;
            margin-bottom: 8px;
          }

          .color-strip {
            display: flex;
            align-items: center;
            gap: 6px;
          }

          .swatch {
            width: 24px;
            height: 24px;
            border-radius: 4px;
            border: 1px solid rgba(255,255,255,0.2);
          }

          .swatch.current {
            width: 32px;
            height: 32px;
            border: 2px solid var(--secondary);
          }

          .plus, .arrow {
            color: var(--text-secondary);
            font-size: 12px;
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.warning {
            background: rgba(245, 158, 11, 0.2);
            color: #FBBF24;
          }

          .status-message.reaction {
            background: rgba(255, 90, 54, 0.2);
            color: #FF5A36;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box .equation {
            font-family: 'Courier New', monospace;
            background: rgba(0, 0, 0, 0.3);
            padding: 8px 12px;
            border-radius: 4px;
            margin-bottom: 8px;
            color: ${saltFormationColors.copperSulfide};
          }

          .theory-box p {
            margin: 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp6"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
